import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, User } from "lucide-react";

export default function PrayerComments({ prayerId, onCommentAdded }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/prayers/${prayerId}/comments`);
      setComments(data || []);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [prayerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/prayers/${prayerId}/comments`, { text: text.trim() });
      setText("");
      await load();
      if (onCommentAdded) onCommentAdded();
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[#E5E0D8] pt-4">
      <div className="flex items-center gap-2 text-xs font-bold text-[#5C6661] uppercase tracking-wider mb-3">
        <MessageSquare className="w-3 h-3" /> Discussion ({comments.length})
      </div>

      <div className="space-y-3 mb-4 max-h-60 overflow-y-auto pr-2">
        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#5C6661]" /></div>
        ) : comments.length === 0 ? (
          <div className="text-center py-2 text-xs text-[#8A8F8B] italic">No comments yet. Be the first to encourage!</div>
        ) : (
          comments.map(c => (
            <div key={c.id} className="bg-[#FAF9F6] p-3 rounded-xl border border-[#E5E0D8]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#2C4C3B]">{c.user_name}</span>
                <span className="text-[10px] text-[#8A8F8B]">{new Date(c.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
              </div>
              <p className="text-sm text-[#1C201E] whitespace-pre-line">{c.text}</p>
            </div>
          ))
        )}
      </div>

      <form onSubmit={submit} className="flex gap-2">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Write a message of encouragement..."
          className="flex-1 px-3 py-2 text-sm border border-[#E5E0D8] rounded-full focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-white"
        />
        <button
          disabled={sending || !text.trim()}
          type="submit"
          className="p-2 rounded-full bg-[#2C4C3B] text-white hover:bg-[#1A3326] disabled:opacity-40 transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
